import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Alert
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import api from '../../../services/api';
import { format } from 'date-fns';

const DiscoveryPanel = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [brands, setBrands] = useState([]);
  const [runningJobId, setRunningJobId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    brandId: '',
    targetDomain: '',
    keywords: '',
    cronSchedule: '0 0 * * *' // Default daily
  });

  useEffect(() => {
    fetchJobs();
    fetchBrands();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/discovery');
      setJobs(res.data.data);
    } catch (err) {
      setError('Erro ao carregar rotinas de descoberta');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await api.get('/brands');
      setBrands(res.data.data || []);
    } catch (err) {
      console.error('Erro ao buscar marcas', err);
    }
  };

  const handleToggle = async (jobId) => {
    try {
      await api.put(`/admin/discovery/${jobId}/toggle`);
      setJobs(jobs.map(j => j._id === jobId ? { ...j, isActive: !j.isActive } : j));
      setSuccess('Status alterado com sucesso');
    } catch (err) {
      setError('Erro ao alterar status');
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Certeza que deseja remover esta rotina?")) return;
    try {
      await api.delete(`/admin/discovery/${jobId}`);
      setJobs(jobs.filter(j => j._id !== jobId));
      setSuccess('Rotina excluída com sucesso');
    } catch (err) {
      setError('Erro ao excluir rotina');
    }
  };

  const handleRunNow = async (jobId) => {
    try {
      setRunningJobId(jobId);
      setError('');
      const res = await api.post(`/admin/discovery/${jobId}/run`);
      const data = res.data.data;
      setSuccess(`Pesquisa concluída! Encontrados: ${data.totalFound} | Novos na fila: ${data.totalNew}`);
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao executar pesquisa');
    } finally {
      setRunningJobId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/discovery', formData);
      setSuccess('Nova rotina criada com sucesso!');
      setOpenDialog(false);
      setFormData({ name: '', brandId: '', targetDomain: '', keywords: '', cronSchedule: '0 0 * * *' });
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao criar rotina');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Motor de Descoberta Dork (Google Digger)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Varredura orgânica e automática de redes sociais via Google Search
          </Typography>
        </Box>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Nova Rotina
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Nome do Job</TableCell>
              <TableCell>Marca Alvo</TableCell>
              <TableCell>Alvo (Domain)</TableCell>
              <TableCell>Termos (Dork)</TableCell>
              <TableCell>Frequência (Cron)</TableCell>
              <TableCell>Última Execução</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  Nenhum job de descoberta configurado.
                </TableCell>
              </TableRow>
            ) : (
              jobs.map(job => (
                <TableRow key={job._id}>
                  <TableCell>
                    <Switch 
                      checked={job.isActive} 
                      onChange={() => handleToggle(job._id)} 
                      color="success" 
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {job.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{job.brand?.name || '---'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={job.targetDomain} color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', p: 0.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                      {job.keywords}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={job.cronSchedule} />
                  </TableCell>
                  <TableCell>
                    {job.lastRunAt ? (
                      <Box>
                        <Typography variant="caption" display="block">
                          {format(new Date(job.lastRunAt), 'dd/MM/yy HH:mm')}
                        </Typography>
                        {job.lastError ? (
                          <Typography variant="caption" color="error">Erro!</Typography>
                        ) : (
                          <Typography variant="caption" color="success.main">
                            Última: +{job.lastNewLinksCount} links
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Nunca rodou</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Testar Agora (Run Now)">
                      <IconButton 
                        color="secondary" 
                        onClick={() => handleRunNow(job._id)}
                        disabled={runningJobId === job._id}
                      >
                        {runningJobId === job._id ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir Rotina">
                      <IconButton color="error" onClick={() => handleDelete(job._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de Criação */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Criar Robô de Descoberta</DialogTitle>
        <DialogContent dividers>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField 
              label="Nome de Identificação" 
              fullWidth 
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Ex: Busca Honda Quatro Rodas"
            />

            <FormControl fullWidth required>
              <InputLabel>Marca Vinculada</InputLabel>
              <Select
                value={formData.brandId}
                label="Marca Vinculada"
                onChange={(e) => setFormData({...formData, brandId: e.target.value})}
              >
                {brands.map(b => (
                  <MenuItem key={b._id} value={b._id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField 
              label="Alvo (Target Domain)" 
              fullWidth 
              required
              value={formData.targetDomain}
              onChange={(e) => setFormData({...formData, targetDomain: e.target.value})}
              placeholder="Ex: instagram.com/quatro_rodas"
            />

            <TextField 
              label="Palavras-Chave (Regras do Google)" 
              fullWidth 
              required
              value={formData.keywords}
              onChange={(e) => setFormData({...formData, keywords: e.target.value})}
              placeholder='Ex: "Honda" OR "Civic"'
              helperText='Essa exata string será jogada no Google antes do site:targetDomain'
            />

            <FormControl fullWidth required>
              <InputLabel>Frequência (Cron Schedule)</InputLabel>
              <Select
                value={formData.cronSchedule}
                label="Frequência (Cron Schedule)"
                onChange={(e) => setFormData({...formData, cronSchedule: e.target.value})}
              >
                <MenuItem value="0 * * * *">De hora em hora</MenuItem>
                <MenuItem value="0 */6 * * *">A cada 6 horas</MenuItem>
                <MenuItem value="0 0 * * *">Diariamente (Meia-noite)</MenuItem>
                <MenuItem value="0 0 * * 0">Semanalmente (Domingo)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!formData.name || !formData.brandId || !formData.targetDomain || !formData.keywords}>
            Salvar Job
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiscoveryPanel;
